<template>
  <div class="sornBG lightBG">
    <h1>Change Password</h1>
    <v-container class="col-8">
      <v-row>
        <v-col md="2"></v-col>
        <v-col md="4">
          <v-form ref="form">
            <v-text-field
              :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
              :rules="[rules.required]"
              :type="show1 ? 'text' : 'password'"
              color="blue"
              label="New Password"
              v-model="password"
              prepend-icon="mdi-lock"
              @click:append="show1 = !show1"
              class="mt-8"
              @keyup="checkRequirements"
              tabindex="1"
            ></v-text-field>
            <v-text-field
              :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
              :rules="[rules.required, rules.passwordMatch]"
              :type="show2 ? 'text' : 'password'"
              color="blue"
              label="Reenter New Password"
              v-model="passwordMatch"
              prepend-icon="mdi-lock"
              @click:append="show2 = !show2"
              class="mt-8"
              tabindex="2"
            ></v-text-field>
            <div class="text-center red--text">&nbsp;{{ error }}</div>
          </v-form>
        </v-col>
        <v-col md="6">
          <v-card-title class="ma-auto headline">
            Password Requirements
          </v-card-title>
          <v-card-text>
            <v-row dense class="black--text mb-3">
              <v-icon :color="isLengthValid ? 'green' : 'red'" left>{{
                isLengthValid ? 'mdi-check-circle' : 'mdi-close-circle'
              }}</v-icon>
              <div class="black--text body-1">
                at least 8 characters (max 20)
              </div>
            </v-row>
            <v-row dense class="black--text mb-3">
              <v-icon :color="isCaseValid ? 'green' : 'red'" left>{{
                isCaseValid ? 'mdi-check-circle' : 'mdi-close-circle'
              }}</v-icon>
              <div class="black--text body-1">
                at least 1 uppercase and 1 lowercase letter
              </div>
            </v-row>
            <v-row dense class="black--text mb-3">
              <v-icon :color="isNumberValid ? 'green' : 'red'" left>{{
                isNumberValid ? 'mdi-check-circle' : 'mdi-close-circle'
              }}</v-icon>
              <div class="black--text body-1">
                at least 1 number
              </div>
            </v-row>
            <v-row dense class="black--text mb-3">
              <v-icon :color="isSpecialValid ? 'green' : 'red'" left>{{
                isSpecialValid ? 'mdi-check-circle' : 'mdi-close-circle'
              }}</v-icon>
              <div class="black--text body-1">
                at least 1 special character ( !"#$%&'()*+,-./:;<=>?@^_`{|}~)
              </div>
            </v-row>
          </v-card-text>
        </v-col>
      </v-row>
      <v-row class="col-8 ma-auto">
        <div>{{ passStrengthWord }}</div>
        <v-progress-linear
          rounded
          background-color="grey"
          background-opacity="0.5"
          :color="passStrengthColor"
          :value="passStrengthValue"
        ></v-progress-linear>
        <v-expansion-panels flat>
          <v-expansion-panel
            style="background-color:rgba(0, 0, 0, 0); border-color:rgba(0, 0, 0, 0)"
          >
            <v-expansion-panel-header class="pl-0" color="rgba(0, 0, 0, 0)">
              Details
            </v-expansion-panel-header>
            <v-expansion-panel-content style="white-space: pre-wrap">
              {{ passStrengthDetails }}
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-row>
      <v-row class="col-6 ma-auto">
        <v-col class="text-right">
          <v-btn class="mr-5" color="primary" block @click="cancel">
            Cancel
          </v-btn>
        </v-col>
        <v-col class="text-right">
          <v-btn
            :disabled="
              !isLengthValid ||
                !isCaseValid ||
                !isNumberValid ||
                !isSpecialValid ||
                password !== passwordMatch
            "
            class="mr-5"
            color="primary"
            block
            type="submit"
            @click="savePassword"
          >
            Save
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import zxcvbn from 'zxcvbn'

export default {
  name: 'appSelection',
  data() {
    return {
      show1: false,
      show2: false,
      password: '',
      passwordMatch: '',
      rules: {
        required: value => !!value || 'Required.',
        min: v => v.length >= 8 || 'Min 8 Characters',
        minMax: v => v.length >= 8 && v.length <= 20,
        containsDigit: v => (v.match(/(?=.*\d)/) ? true : false),
        containsSpecialChar: v =>
          v.match(/(?=.[ !"#$%&'()*+,-./:;<=>?@^_`{|}~])/) ? true : false,
        containsUpperAndLowerCase: v =>
          v.match(/(?=.*[A-Z])/) && v.match(/(?=.*[a-z])/) ? true : false,
        passwordMatch: () =>
          this.password === this.passwordMatch || 'The passwords do not match',
      },
      error: '',
      loading: false,
      isLengthValid: false,
      isNumberValid: false,
      isCaseValid: false,
      isSpecialValid: false,
      passStrengthValue: 0,
      passStrengthWord: 'Invalid',
      passStrengthColor: 'red',
      passStrengthDetails:
        'Please enter a valid password to see password strength.',
    }
  },
  async mounted() {},
  methods: {
    ...mapActions(['getApps', 'getUser', 'logoffUser', 'changePassword']),
    ...mapMutations(['setAccountComponent']),
    checkRequirements() {
      this.passStrengthDetails = ''
      this.isLengthValid = this.rules.minMax(this.password)
      this.isNumberValid = this.rules.containsDigit(this.password)
      this.isCaseValid = this.rules.containsUpperAndLowerCase(this.password)
      this.isSpecialValid = this.rules.containsSpecialChar(this.password)
      if (
        !this.isLengthValid ||
        !this.isNumberValid ||
        !this.isCaseValid ||
        !this.isSpecialValid
      ) {
        this.passStrengthValue = 0
        this.passStrengthWord = 'Invalid'
        this.passStrengthColor = 'red'
        this.passStrengthDetails =
          'Please enter a valid password to see password strength.'
        return
      }
      let strengthResult = zxcvbn(this.password)
      switch (strengthResult.score) {
        case 0:
          this.passStrengthValue = 10
          this.passStrengthWord = 'Very Weak'
          this.passStrengthColor = 'red'
          break
        case 1:
          this.passStrengthValue = 25
          this.passStrengthWord = 'Weak'
          this.passStrengthColor = 'orange'
          break
        case 2:
          this.passStrengthValue = 50
          this.passStrengthWord = 'Average'
          this.passStrengthColor = 'yellow'
          break
        case 3:
          this.passStrengthValue = 75
          this.passStrengthWord = 'Strong'
          this.passStrengthColor = 'light-green'
          break
        case 4:
          this.passStrengthValue = 100
          this.passStrengthWord = 'Very Strong'
          this.passStrengthColor = 'green'
          break
      }
      this.passStrengthDetails =
        'Estimated Crack Time: ' +
        strengthResult.crack_times_display.online_no_throttling_10_per_second
      if (strengthResult.feedback.warning) {
        this.passStrengthDetails =
          this.passStrengthDetails +
          '\n\n Warning: ' +
          strengthResult.feedback.warning
      }
      // leaving out the suggestions piece right now because sometimes it says things like 'Capitalization doesn't help very much' and it contradicts our rules
      // if(strengthResult.feedback.suggestions.length > 0){
      //   this.passStrengthDetails = this.passStrengthDetails + '\n\n Suggestions: \n'
      //   for(let suggestion of strengthResult.feedback.suggestions){
      //     this.passStrengthDetails = this.passStrengthDetails + '\t' + suggestion + '\n'
      //   }
      // }
    },
    async savePassword() {
      //if (this.password.length >= 8 && this.password === this.passwordMatch) {
      this.loading = true
      try {
        await this.changePassword({
          user_password: this.passwordMatch,
          force_password_flag: false,
        }).then(() => {
          this.password = ''
          this.passwordMatch = ''
          this.setAccountComponent('AccountMain')
        })
      } catch (e) {
        this.error = e.response.data.error
        this.password = ''
        this.passwordMatch = ''
        this.$refs.form.reset()
      }
      this.loading = false
    },
    cancel() {
      this.password = ''
      this.passwordMatch = ''
      this.$refs.form.reset()
      this.setAccountComponent('AccountMain')
    },
  },
  computed: {
    ...mapState(['applications', 'user', 'userToAppMappings']),
  },
  watch: {},
}
</script>

<style>
.sornBG {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}
.center {
  align-items: center;
}

.v-expansion-panel-header__icon {
  margin-left: 0 !important;
}
</style>
